import React, { useState, useEffect, useRef } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, ScrollView, TouchableOpacity, View } from 'react-native'

import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';
import Icon from '../../../components/IcomoonIcon';
import { Icon as SvgIcon } from '../../../components/Icon';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import QRScannerModal from './QRScannerModal';
import { UnitAutocomplete } from '../Admin/Fees/Fees/AddPaymentModal';

import { useForm } from 'react-hook-form';
import Input from '../../../components/Forms/Input';
import Select from '../../../components/Forms/Select';
import IconButton from '../../../components/IconButton';
import ImageCapture from '../../../components/ImageCapture';
import useFormSubmit from '../../../hooks/useFormSubmit';
import useVoiceEntryForm from './useVoiceEntryForm';


// Borde gris de los campos del formulario.
const FIELD_BORDER = 'border border-gray-200';

export default function GuardRegistrationScreen({ navigation, route }) {
  const { communityId } = route.params

  const { control, reset, handleSubmit, setValue } = useForm();

  const [selectedUnit, setSelectedUnit] = useState();
  const [showScanQRModal, setShowScanQRModal] = useState(false);
  const [takingPicture, setTakingPicture] = useState(false);
  // Fotos tomadas: miniaturas en fila y se mandan en `images`.
  const [photos, setPhotos] = useState<any[]>([]);

  function addPhoto(picture) {
    const next = [...photos, picture]
    setPhotos(next)
    setValue('images', next)
  }

  function removePhoto(index: number) {
    const next = photos.filter((_, i) => i !== index)
    setPhotos(next)
    setValue('images', next)
  }

  const unitRef = useRef<any>();

  const { recognizing, processing, toggleVoice, isHighlighted } = useVoiceEntryForm({
    communityId,
    setValue,
    unitRef,
  });

  const highlightStyle = (field: string) =>
    isHighlighted(field) ? 'border-2 border-secondary' : '';

  // Todos los campos como rectángulo redondeado (el Input en la app es píldora por defecto),
  // igual que los selectores, con borde gris para que se distingan con poco contraste
  // (sol en la caseta); el resaltado del dictado lo reemplaza mientras está activo.
  const fieldStyle = (field: string) => ({
    subcontainer: `rounded-lg ${highlightStyle(field) || FIELD_BORDER}`,
    input: `rounded-lg`,
  });


  function onUnitSelected(unit) {
    setSelectedUnit(unit)
    setValue("unit_id", unit ? unit.uuid : null)
  }

  function onEntryRegistered(){
    unitRef.current?.reset();
    setPhotos([]);

    reset({
      guest_type: 'guest',
    })
  }

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onEntryRegistered,
    onError: (error) => { },
    successToastMessage: "Registro creado",
    endpoint: `/communities/${communityId}/entry-logs`,
  })

  return (
    // Fondo gris con el formulario en una card blanca, como la pantalla de Acceso.
    <View style={tw`flex-1 bg-background`}>
      {/* El teclado no debe tapar los campos de abajo (placas, comentarios) en pantallas
          chicas. "padding" en las dos plataformas: con edge-to-edge (Expo 56) Android ya no
          redimensiona la pantalla solo. */}
      <KeyboardAvoidingView style={tw`flex-1`} behavior="padding">
      <SafeAreaView style={tw`flex-1`} edges={['top']}>
        <ScrollView
          style={tw`flex-1`}
          contentContainerStyle={tw`px-4 pb-30`}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={tw`mt-3 mb-4 items-center`}>
            <Text style={tw`text-xl`}>Registro de entrada</Text>
          </View>

          {/* Acciones rápidas: dos botones altos del mismo ancho, QR a la izquierda y dictado a la derecha. */}
          <View style={tw`row mb-6`}>
            <TouchableOpacity
              onPress={() => setShowScanQRModal(true)}
              style={tw`flex-1 h-28 mr-3 rounded-2xl bg-primary items-center justify-center`}
            >
              <Icon name='qr' size={34} color="white" />
              <Text style={tw`text-white font-bold mt-2`}>Escanear QR</Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={toggleVoice}
              disabled={processing}
              style={tw.style(
                `flex-1 h-28 rounded-2xl items-center justify-center`,
                recognizing ? `bg-red-500` : `bg-secondary`,
                processing && `opacity-60`,
              )}
            >
              {processing ? (
                <>
                  <ActivityIndicator color="white" size="large" />
                  <Text style={tw`text-white font-bold mt-2`}>Procesando...</Text>
                </>
              ) : (
                <>
                  <Ionicons name={recognizing ? 'stop-circle-outline' : 'mic-outline'} size={34} color="white" />
                  <Text style={tw`text-white font-bold mt-2`}>
                    {recognizing ? 'Detener y llenar' : 'Dictar entrada'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>

          {/* Formulario en card */}
          <View style={tw`bg-white border border-gray-200 rounded-2xl px-4 pt-5 pb-4`}>
          <View style={tw.style(`mb-4 z-20 rounded-lg`, highlightStyle('unit'))}>
            <Text style={tw`text-gray-500`}>Vivienda</Text>

            <UnitAutocomplete
              ref={unitRef}
              communityId={communityId}
              onUnitSelected={onUnitSelected}
              nestedScrollEnabled={true}
              inputStyle={`rounded-lg h-12 ${FIELD_BORDER}`}
            />
          </View>

          <View style={tw``}>
            <Text style={tw`text-gray-500`}>Nombre Visitante</Text>

            <Input
              control={control}
              name='guest_name'
              placeholder=''
              style={fieldStyle('guest_name')}
              rules={{ required: true }}
            />
          </View>

          <View style={tw`z-0`}>
            <Text style={tw`text-gray-500`}>Tipo de visitante</Text>

            <Select
              control={control}
              name='guest_type'
              placeholder={{ value: "", label: "Seleccionar" }}
              items={[
                { value: "provider", label: "Proveedor" },
                { value: "resident", label: "Residente" },
                { value: "guest", label: "Visitante" },
                { value: "tenant", label: "Arrendatario" },
                { value: "other", label: "Otro" },
              ]}
              value={"guest"}
              style={{ input: tw`${FIELD_BORDER}` }}
            />
          </View>

          <View style={tw``}>
            <Text style={tw`text-gray-500`}>Marca/Modelo Auto</Text>

            <Input
              control={control}
              name='vehicle_brand'
              placeholder=''
              style={fieldStyle('vehicle_brand')}
            />
          </View>

          <View style={tw`w-50`}>
            <Text style={tw`text-gray-500`}>Placas Auto</Text>

            <Input
              control={control}
              name='vehicle_plate_number'
              placeholder=''
              style={fieldStyle('vehicle_plate_number')}
            />
          </View>

          <View style={tw``}>
            <Text style={tw`text-gray-500`}>Comentarios</Text>

            <Input
              control={control}
              name='comments'
              placeholder=''
              multiline
              numberOfLines={3}
              style={fieldStyle('comments')}
            />
          </View>

          {/* Fotos: miniaturas a la izquierda y al final el botón cuadrado para tomar otra. */}
          <View style={tw`row flex-wrap mb-5`}>
            {photos.map((photo, index) => (
              <View key={photo.image?.uri ?? index} style={tw`w-20 h-20 mr-3 mb-3`}>
                <Image source={{ uri: photo.image?.uri }} style={tw`w-20 h-20 rounded-xl`} contentFit="cover" />
                <TouchableOpacity
                  onPress={() => removePhoto(index)}
                  style={tw`absolute -top-2 -right-2 bg-white rounded-full`}
                  accessibilityLabel="Quitar foto"
                >
                  <Ionicons name="close-circle" size={22} color={tw.color('gray-500')} />
                </TouchableOpacity>
              </View>
            ))}

            <TouchableOpacity
              onPress={() => setTakingPicture(true)}
              style={tw`w-20 h-20 mb-3 items-center justify-center bg-primary-50 border border-primary rounded-xl`}
              accessibilityLabel={photos.length ? 'Tomar otra foto' : 'Tomar foto'}
            >
              <SvgIcon name="picture" size={26} />
              <Text style={tw`text-primary-600 text-xs font-500 mt-1`}>{photos.length ? 'Otra' : 'Foto'}</Text>
            </TouchableOpacity>
          </View>

          <IconButton
            title={"Registrar entrada"}
            disabled={isSubmitting}
            onPress={handleSubmit(submitForm)}
          />
          </View>

        </ScrollView>
      </SafeAreaView>
      </KeyboardAvoidingView>

      {
        showScanQRModal &&
          <QRScannerModal
            communityId={communityId}
            onRequestClose={()=>setShowScanQRModal(false)}
          />
      }

      {
        takingPicture &&
          <ImageCapture
            onRequestClose={() => setTakingPicture(false)}
            onPictureTaken={picture => {
              setTakingPicture(false)
              addPhoto(picture)
            }}
          />
      }

    </View>
  );
}
